import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Phone, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PHONE_CODE_LIST, getCountryByTimezone, getCountryByCode } from '../config/countries';
import Alert from '../components/common/Alert';
import KeyboardDismissView from '../components/common/KeyboardDismissView';
import { useFormNavigation } from '../hooks/useFormNavigation';
import { saveCredentials, loadCredentials, clearCredentials } from '../utils/rememberMe';
import { openExternalUrl } from '../services/native';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import AppleLoginButton from '../components/auth/AppleLoginButton';

// Detect phone code from OS timezone (e.g. Asia/Kolkata → IN → +91), fallback to +91
const getDefaultPhoneCode = () => {
  const countryCode = getCountryByTimezone();
  if (countryCode) {
    const country = getCountryByCode(countryCode);
    if (country) return country.phoneCode;
  }
  return '+91';
};

const DEFAULT_PHONE_CODE = getDefaultPhoneCode();

// Detect mode from first character of identifier
const detectMode = (value) => {
  if (!value) return 'default';
  const first = value.charAt(0);
  if (first === '+' || /\d/.test(first)) return 'phone';
  return 'email';
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, appleLogin, error, clearError } = useAuth();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [inputMode, setInputMode] = useState('default'); // 'default' | 'email' | 'phone'
  const [selectedPhoneCode, setSelectedPhoneCode] = useState(DEFAULT_PHONE_CODE);
  const [phoneCodeOpen, setPhoneCodeOpen] = useState(false);
  const [phoneCodeSearch, setPhoneCodeSearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // One-shot notice set when the session was ended by the server (e.g. the
  // account was deactivated by an admin).
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    try {
      const n = sessionStorage.getItem('authNotice');
      if (n) { setNotice(n); sessionStorage.removeItem('authNotice'); }
    } catch { /* sessionStorage unavailable */ }
  }, []);
  const rememberMe = true; // Always remember credentials
  const phoneCodeRef = useRef(null);
  const phoneCodeSearchRef = useRef(null);

  const { registerField, focusNext } = useFormNavigation({
    onSubmit: () => document.getElementById('login-submit')?.click()
  });

  // Load saved credentials on mount
  useEffect(() => {
    const saved = loadCredentials();
    if (saved) {
      const mode = detectMode(saved.identifier);
      if (mode === 'phone') {
        // Extract phone code and number from E.164 string
        const match = PHONE_CODE_LIST.find(c => saved.identifier.startsWith(c.phoneCode));
        if (match) {
          setSelectedPhoneCode(match.phoneCode);
          setFormData({ identifier: saved.identifier.slice(match.phoneCode.length), password: saved.password });
        } else {
          setFormData({ identifier: saved.identifier, password: saved.password });
        }
        setInputMode('phone');
      } else {
        setFormData({ identifier: saved.identifier, password: saved.password });
        setInputMode(mode);
      }
      // Credentials loaded from storage
    }
  }, []);

  // Close phone code dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (phoneCodeRef.current && !phoneCodeRef.current.contains(e.target)) {
        setPhoneCodeOpen(false);
        setPhoneCodeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (phoneCodeOpen && phoneCodeSearchRef.current) {
      phoneCodeSearchRef.current.focus();
    }
  }, [phoneCodeOpen]);

  // Restore cursor to end of input after mode switch (layout change moves cursor to 0)
  useEffect(() => {
    if (inputMode === 'default') return;
    requestAnimationFrame(() => {
      const el = document.querySelector('input[name="identifier"]');
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }, [inputMode]);

  // Filtered phone code list
  const filteredPhoneCodes = useMemo(() => {
    if (!phoneCodeSearch) return PHONE_CODE_LIST;
    const q = phoneCodeSearch.toLowerCase();
    return PHONE_CODE_LIST.filter(c =>
      c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)
    );
  }, [phoneCodeSearch]);

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    const newMode = detectMode(value);

    // In phone mode, only allow digits
    if (newMode === 'phone') {
      const digits = value.replace(/[^\d]/g, '');
      setFormData({ ...formData, identifier: digits });
    } else {
      setFormData({ ...formData, identifier: value });
    }
    setInputMode(newMode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let loginPayload;
      let savedIdentifier;

      if (inputMode === 'phone') {
        const fullPhone = selectedPhoneCode + formData.identifier;
        loginPayload = { phone: fullPhone, password: formData.password };
        savedIdentifier = fullPhone;
      } else {
        loginPayload = { email: formData.identifier, password: formData.password };
        savedIdentifier = formData.identifier;
      }

      await login(loginPayload);

      // Save or clear credentials based on Remember Me checkbox
      if (rememberMe) {
        saveCredentials(savedIdentifier, formData.password);
      } else {
        clearCredentials();
      }

      navigate('/');
    } catch {
      // Error is set by AuthContext and displayed via Alert
      // Stay on page - don't navigate or clear form
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-surface-50 flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mb-6 rounded-full"
            />
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Take control of<br />your finances
            </h1>
            <p className="text-surface-400 text-lg mt-4 max-w-md">
              Track income and expenses with ease. Make smarter financial decisions.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4 mt-8">
            {[
              'Track income & expenses',
              'Category-wise reports',
              'Multi-account support',
              'Works offline (PWA)'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-surface-300">
                <div className="w-5 h-5 rounded-full bg-income-500/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-income-500" />
                </div>
                {feature}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Right Panel - Login Form */}
      <KeyboardDismissView className="flex-1 overflow-y-auto safe-area-top">
        <div className="min-h-full flex items-start xl:items-center justify-center p-6 pt-10 sm:p-12">
        <div className="w-full max-w-md pb-8">
          {/* Mobile Logo */}
          <div className="xl:hidden text-center mb-6">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mx-auto mb-3 rounded-2xl"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900">
              Welcome back
            </h2>
            <p className="text-surface-500 mt-1.5">
              Your finances. Private. Secure.
            </p>
          </div>

          {/* Session-ended notice (e.g. account deactivated) */}
          {notice && (
            <Alert
              variant="error"
              message={notice}
              onClose={() => setNotice(null)}
              className="mb-4 break-words"
            />
          )}

          {/* Auth error */}
          {error && (
            <Alert
              variant="error"
              message={error}
              onClose={clearError}
              className="mb-4 break-words"
            />
          )}

          {/* Social Sign-In */}
          <div className="space-y-3 mb-6">
            <GoogleLoginButton
              onSuccess={() => navigate('/')}
              onError={() => {}}
              text="signin_with"
            />
            <AppleLoginButton
              onSuccess={() => navigate('/')}
              onError={() => {}}
              text="sign_in"
            />
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-surface-50 text-surface-400">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email or Phone — adaptive input */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                {inputMode === 'phone' ? 'Phone number' : inputMode === 'email' ? 'Email address' : 'Email or phone number'}
              </label>
              <div className="flex gap-1.5">
                {/* Phone code dropdown — only visible in phone mode */}
                {inputMode === 'phone' && (
                  <div className="relative shrink-0" ref={phoneCodeRef}>
                    <button
                      type="button"
                      onClick={() => setPhoneCodeOpen(!phoneCodeOpen)}
                      className="flex items-center gap-0.5 px-2.5 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all whitespace-nowrap"
                    >
                      <span className="text-sm font-medium">{selectedPhoneCode}</span>
                      <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
                    </button>
                    {phoneCodeOpen && (
                      <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-surface-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-surface-100">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                            <input
                              ref={phoneCodeSearchRef}
                              type="text"
                              value={phoneCodeSearch}
                              onChange={(e) => setPhoneCodeSearch(e.target.value)}
                              placeholder="Search country..."
                              className="w-full pl-9 pr-3 py-2 text-sm bg-surface-50 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-surface-900"
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-48">
                          {filteredPhoneCodes.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => {
                                setSelectedPhoneCode(c.phoneCode);
                                setPhoneCodeOpen(false);
                                setPhoneCodeSearch('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-50 transition-colors flex items-center justify-between ${
                                selectedPhoneCode === c.phoneCode ? 'bg-surface-50 font-medium' : ''
                              }`}
                            >
                              <span className="truncate">{c.name}</span>
                              <span className="text-surface-500 ml-2 shrink-0">{c.phoneCode}</span>
                            </button>
                          ))}
                          {filteredPhoneCodes.length === 0 && (
                            <p className="px-3 py-2 text-sm text-surface-400">No countries found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Adaptive input */}
                <div className="relative flex-1">
                  {inputMode === 'phone' ? (
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                  ) : (
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                  )}
                  <input
                    ref={(el) => registerField('identifier', el)}
                    type={inputMode === 'phone' ? 'tel' : inputMode === 'email' ? 'email' : 'text'}
                    inputMode={inputMode === 'phone' ? 'tel' : undefined}
                    name="identifier"
                    value={formData.identifier}
                    onChange={handleIdentifierChange}
                    placeholder={inputMode === 'phone' ? '9876543210' : inputMode === 'email' ? 'you@example.com' : 'Email or phone number'}
                    enterKeyHint="next"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('identifier'))}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-surface-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm hover:underline"
                  style={{ color: 'var(--accent-bg)' }}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('password', el)}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  enterKeyHint="done"
                  className="w-full pl-12 pr-12 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              id="login-submit"
              type="submit"
              className={`w-full py-3.5 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group ${
                loading ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-px active:scale-[0.98]'
              }`}
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)',
                boxShadow: !loading ? '0 4px 14px -3px rgba(59, 130, 246, 0.4)' : undefined
              }}
              disabled={loading}
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-surface-500 text-sm mt-6">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-medium hover:underline"
              style={{ color: 'var(--accent-bg)' }}
            >
              Register here
            </Link>
          </p>

          <div className="flex items-center justify-center gap-3 mt-4 text-xs text-surface-400">
            <button
              onClick={() => openExternalUrl('https://hisabjourney.com/privacy')}
              className="hover:text-surface-600 underline"
            >
              Privacy Policy
            </button>
            <span>·</span>
            <button
              onClick={() => openExternalUrl('https://hisabjourney.com/terms')}
              className="hover:text-surface-600 underline"
            >
              Terms of Service
            </button>
          </div>
        </div>
        </div>
      </KeyboardDismissView>
    </div>
  );
}
